import type { ColDef, GridOptions, ICellRendererParams } from '../../../components/ui/table';
import CountryFlag from '../../../components/shared/CountryFlag';
import { collectUniqueScalarValues } from '../../../components/shared/GridColumnFilterPanel';
import { AUDIT_EXPORT_COLUMNS, formatAuditDateTime } from '../../../lib/auditColumnDefs';
import {
  CategoryChipCellRenderer,
  ChipListCell,
  EditDeleteActionsCellRenderer,
  GridCellWrap,
  NameAvatarCell,
  StatusBadgeCellRenderer,
  SubtitleCellRenderer,
} from '../../../components/shared/agGridCells';
import { commonGridOptions } from '../../../lib/agGridOptions';
import type { ExcelColumn } from '../../../utils/excelExport';
import { TERMINALS_COLUMN_FILTER_LABELS } from '../constants/constants';
import type {
  AcquirerTerminalGridRow,
  AcquirerTerminalRow,
  MappingGridRow,
  MerchantTerminalGridRow,
  MerchantTerminalRow,
  TerminalRow,
  TerminalsColumnFilterKey,
  TerminalsGridContext,
} from '../models/types';
import {
  collectTerminalCurrencyFilterOptions,
  formatAcquirerTerminalIdLabel,
  formatMappingIdLabel,
  formatMappingPriorityLabel,
  formatMerchantTerminalIdLabel,
  formatSuccessTransactionVolume,
  resolveDisplayName,
} from '../utils/utils';

export {
  GRID_PAGE_SIZE,
  GRID_PAGE_SIZE_SELECTOR,
} from '../../../components/shared/agGridCells';

/** Shared with createMappingColumnDefs + Excel export — keep header strings in one place. */
export const MAPPING_COLUMN_HEADERS = {
  merchantTerminal: 'Merchant Terminal',
  acquirerTerminal: 'Acquirer Terminal',
  mappingId: 'ID',
  merchantName: 'Merchant Name',
  acquirerName: 'Acquirer Name',
  volume: 'Volume',
  status: 'Status',
  priority: 'Priority',
} as const;

/** Same columns and visual order as the mappings grid (Action excluded). Status is pinned right. */
export const MAPPING_EXPORT_COLUMNS: ExcelColumn[] = [
  { header: MAPPING_COLUMN_HEADERS.mappingId, key: 'id' },
  { header: MAPPING_COLUMN_HEADERS.priority, key: 'priority' },
  { header: MAPPING_COLUMN_HEADERS.merchantTerminal, key: 'merchantTerminalName' },
  { header: MAPPING_COLUMN_HEADERS.acquirerTerminal, key: 'acquirerTerminalName' },
  { header: MAPPING_COLUMN_HEADERS.merchantName, key: 'merchantName' },
  { header: MAPPING_COLUMN_HEADERS.acquirerName, key: 'acquirerName' },
  { header: MAPPING_COLUMN_HEADERS.volume, key: 'volume' },
  ...AUDIT_EXPORT_COLUMNS,
  { header: MAPPING_COLUMN_HEADERS.status, key: 'status' },
];

export const MERCHANT_TERMINAL_COLUMN_HEADERS = {
  merchantTerminalId: 'ID',
  name: 'Name',
  merchantName: 'Merchant Name',
  country: 'Country',
  currencies: 'Currencies',
  flow: 'Flow',
  industry: 'Industry',
  traffic: 'Traffic',
  successTransactionVolume: 'Volume',
  notes: 'Notes',
} as const;

/** Same columns and visual order as the merchant-terminals grid (Action excluded). */
export const MERCHANT_TERMINAL_EXPORT_COLUMNS: ExcelColumn[] = [
  { header: MERCHANT_TERMINAL_COLUMN_HEADERS.merchantTerminalId, key: 'terminalId' },
  { header: MERCHANT_TERMINAL_COLUMN_HEADERS.name, key: 'name' },
  { header: MERCHANT_TERMINAL_COLUMN_HEADERS.merchantName, key: 'merchantName' },
  { header: MERCHANT_TERMINAL_COLUMN_HEADERS.country, key: 'country' },
  { header: MERCHANT_TERMINAL_COLUMN_HEADERS.currencies, key: 'currencies' },
  { header: MERCHANT_TERMINAL_COLUMN_HEADERS.flow, key: 'flow' },
  { header: MERCHANT_TERMINAL_COLUMN_HEADERS.industry, key: 'industry' },
  { header: MERCHANT_TERMINAL_COLUMN_HEADERS.traffic, key: 'traffic' },
  {
    header: MERCHANT_TERMINAL_COLUMN_HEADERS.successTransactionVolume,
    key: 'successTransactionVolume',
  },
  { header: MERCHANT_TERMINAL_COLUMN_HEADERS.notes, key: 'notes' },
  ...AUDIT_EXPORT_COLUMNS,
];

export const ACQUIRER_TERMINAL_COLUMN_HEADERS = {
  acquirerTerminalId: 'ID',
  name: 'Name',
  acquirerName: 'Acquirer Name',
  country: 'Country',
  currencies: 'Currencies',
  flow: 'Flow',
  industry: 'Industry',
  traffic: 'Traffic',
  successTransactionVolume: 'Volume',
} as const;

/** Same columns and visual order as the acquirer-terminals grid (Action excluded). */
export const ACQUIRER_TERMINAL_EXPORT_COLUMNS: ExcelColumn[] = [
  { header: ACQUIRER_TERMINAL_COLUMN_HEADERS.acquirerTerminalId, key: 'terminalId' },
  { header: ACQUIRER_TERMINAL_COLUMN_HEADERS.name, key: 'name' },
  { header: ACQUIRER_TERMINAL_COLUMN_HEADERS.acquirerName, key: 'acquirerName' },
  { header: ACQUIRER_TERMINAL_COLUMN_HEADERS.country, key: 'country' },
  { header: ACQUIRER_TERMINAL_COLUMN_HEADERS.currencies, key: 'currencies' },
  { header: ACQUIRER_TERMINAL_COLUMN_HEADERS.flow, key: 'flow' },
  { header: ACQUIRER_TERMINAL_COLUMN_HEADERS.industry, key: 'industry' },
  { header: ACQUIRER_TERMINAL_COLUMN_HEADERS.traffic, key: 'traffic' },
  {
    header: ACQUIRER_TERMINAL_COLUMN_HEADERS.successTransactionVolume,
    key: 'successTransactionVolume',
  },
  ...AUDIT_EXPORT_COLUMNS,
];

type TerminalsFilterField = {
  key: TerminalsColumnFilterKey;
  label: string;
  options: Array<string | { value: string; label: string }>;
  type?: 'select' | 'dateRange';
};

const CREATED_AT_FILTER_FIELD: TerminalsFilterField = {
  key: 'createdAt',
  label: TERMINALS_COLUMN_FILTER_LABELS.createdAt,
  type: 'dateRange',
  options: [],
};

function joinFilterValues(values?: string[]) {
  return (values ?? []).filter(Boolean).join(' ');
}

function successTransactionVolumeColumnDef<
  T extends { successTransactionVolume?: number },
>(headerName: string): ColDef<T> {
  return {
    colId: 'successTransactionVolume',
    headerName,
    width: 140,
    valueGetter: (params) => params.data?.successTransactionVolume ?? 0,
    valueFormatter: (params) =>
      formatSuccessTransactionVolume(Number(params.value ?? 0)),
    cellRenderer: (params: ICellRendererParams<T, number>) => (
      <SubtitleCellRenderer
        {...(params as unknown as ICellRendererParams<T, string>)}
        value={formatSuccessTransactionVolume(Number(params.value ?? 0))}
      />
    ),
    filter: {
      component: 'agNumberColumnFilter',
      doesFilterPass: () => true,
    },
    filterParams: {
      filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
      maxNumConditions: 1,
      buttons: ['clear', 'apply'],
      closeOnApply: true,
    },
  };
}

function createdAtColumnDef<T extends { createdAt?: string | null }>(): ColDef<T> {
  return {
    colId: 'createdAt',
    headerName: TERMINALS_COLUMN_FILTER_LABELS.createdAt,
    width: 180,
    cellClass: 'text-muted-foreground',
    valueGetter: (params) => params.data?.createdAt ?? null,
    valueFormatter: (params) => formatAuditDateTime(String(params.value ?? '')),
    filter: {
      component: 'agDateColumnFilter',
      doesFilterPass: () => true,
    },
    filterParams: {
      filterOptions: ['equals', 'inRange'],
      maxNumConditions: 1,
      buttons: ['clear', 'apply'],
      closeOnApply: true,
    },
  };
}

function updatedAtColumnDef<T extends { updatedAt?: string | null }>(): ColDef<T> {
  return {
    colId: 'updatedAt',
    headerName: 'Updated At',
    width: 180,
    cellClass: 'text-muted-foreground',
    sortable: true,
    filter: false,
    valueGetter: (params) => params.data?.updatedAt ?? null,
    valueFormatter: (params) => formatAuditDateTime(String(params.value ?? '')),
  };
}

/** Filter fields match createMappingColumnDefs order/labels (Action excluded). */
export function buildMappingColumnFilterFields(
  sourceRows: TerminalRow[],
  lookups: {
    merchantById: Record<number, string>;
    acquirerById: Record<number, string>;
  },
): TerminalsFilterField[] {
  const rows = sourceRows as unknown as Array<Record<string, unknown>>;
  return [
    {
      key: 'merchantTerminal',
      label: TERMINALS_COLUMN_FILTER_LABELS.merchantTerminal,
      options: collectUniqueScalarValues(rows, (row) =>
        String(row.merchantTerminalName || '—'),
      ),
    },
    {
      key: 'acquirerTerminal',
      label: TERMINALS_COLUMN_FILTER_LABELS.acquirerTerminal,
      options: collectUniqueScalarValues(rows, (row) =>
        String(row.acquirerTerminalName || '—'),
      ),
    },
    {
      key: 'mappingId',
      label: TERMINALS_COLUMN_FILTER_LABELS.mappingId,
      options: collectUniqueScalarValues(rows, (row) =>
        formatMappingIdLabel(Number(row.id) || 0),
      ),
    },
    {
      key: 'merchant',
      label: TERMINALS_COLUMN_FILTER_LABELS.merchant,
      options: collectUniqueScalarValues(rows, (row) =>
        resolveDisplayName(
          row.merchantName as string | undefined,
          lookups.merchantById,
          Number(row.merchantId) || 0,
        ),
      ),
    },
    {
      key: 'acquirer',
      label: TERMINALS_COLUMN_FILTER_LABELS.acquirer,
      options: collectUniqueScalarValues(rows, (row) =>
        resolveDisplayName(
          row.acquirerName as string | undefined,
          lookups.acquirerById,
          Number(row.acquirerId) || 0,
        ),
      ),
    },
    {
      key: 'volume',
      label: TERMINALS_COLUMN_FILTER_LABELS.volume,
      options: collectUniqueScalarValues(rows, (row) =>
        formatSuccessTransactionVolume(Number(row.volume) || 0),
      ),
    },
    {
      key: 'status',
      label: TERMINALS_COLUMN_FILTER_LABELS.status,
      options: collectUniqueScalarValues(rows, (row) => String(row.status || '—')),
    },
    {
      key: 'priority',
      label: TERMINALS_COLUMN_FILTER_LABELS.priority,
      options: collectUniqueScalarValues(rows, (row) =>
        formatMappingPriorityLabel(
          row.priority != null ? Number(row.priority) : null,
        ),
      ),
    },
    CREATED_AT_FILTER_FIELD,
  ];
}

/** Filter fields match createMerchantTerminalColumnDefs order/labels (Action excluded). */
export function buildMerchantTerminalColumnFilterFields(
  sourceRows: MerchantTerminalRow[],
  lookups: {
    merchantById: Record<number, string>;
    countryNameById: Record<number, string>;
    currencyById: Record<number, string>;
  },
): TerminalsFilterField[] {
  const rows = sourceRows as unknown as Array<Record<string, unknown>>;
  return [
    {
      key: 'merchantTerminalId',
      label: TERMINALS_COLUMN_FILTER_LABELS.merchantTerminalId,
      options: collectUniqueScalarValues(rows, (row) =>
        formatMerchantTerminalIdLabel(Number(row.terminalId) || 0),
      ),
    },
    {
      key: 'name',
      label: TERMINALS_COLUMN_FILTER_LABELS.name,
      options: collectUniqueScalarValues(rows, (row) => String(row.name || '—')),
    },
    {
      key: 'merchant',
      label: TERMINALS_COLUMN_FILTER_LABELS.merchant,
      options: collectUniqueScalarValues(rows, (row) =>
        resolveDisplayName(
          row.merchantName as string | undefined,
          lookups.merchantById,
          Number(row.merchantId) || 0,
        ),
      ),
    },
    {
      key: 'country',
      label: TERMINALS_COLUMN_FILTER_LABELS.country,
      options: collectUniqueScalarValues(rows, (row) =>
        resolveDisplayName(
          row.countryName as string | undefined,
          lookups.countryNameById,
          Number(row.countryId) || 0,
        ),
      ),
    },
    {
      key: 'currencies',
      label: TERMINALS_COLUMN_FILTER_LABELS.currencies,
      options: collectTerminalCurrencyFilterOptions(sourceRows, lookups.currencyById),
    },
    {
      key: 'flow',
      label: TERMINALS_COLUMN_FILTER_LABELS.flow,
      options: collectUniqueScalarValues(rows, (row) => String(row.flowName || '—')),
    },
    {
      key: 'industry',
      label: TERMINALS_COLUMN_FILTER_LABELS.industry,
      options: collectUniqueScalarValues(rows, (row) => String(row.industryName || '—')),
    },
    {
      key: 'traffic',
      label: TERMINALS_COLUMN_FILTER_LABELS.traffic,
      options: collectUniqueScalarValues(rows, (row) => String(row.trafficName || '—')),
    },
    {
      key: 'notes',
      label: TERMINALS_COLUMN_FILTER_LABELS.notes,
      options: collectUniqueScalarValues(rows, (row) => String(row.notes || '—')),
    },
    CREATED_AT_FILTER_FIELD,
  ];
}

/** Filter fields match createAcquirerTerminalColumnDefs order/labels (Action excluded). */
export function buildAcquirerTerminalColumnFilterFields(
  sourceRows: AcquirerTerminalRow[],
  lookups: {
    acquirerById: Record<number, string>;
    countryNameById: Record<number, string>;
    currencyById: Record<number, string>;
  },
): TerminalsFilterField[] {
  const rows = sourceRows as unknown as Array<Record<string, unknown>>;
  return [
    {
      key: 'acquirerTerminalId',
      label: TERMINALS_COLUMN_FILTER_LABELS.acquirerTerminalId,
      options: collectUniqueScalarValues(rows, (row) =>
        formatAcquirerTerminalIdLabel(Number(row.terminalId) || 0),
      ),
    },
    {
      key: 'name',
      label: TERMINALS_COLUMN_FILTER_LABELS.name,
      options: collectUniqueScalarValues(rows, (row) => String(row.name || '—')),
    },
    {
      key: 'acquirer',
      label: TERMINALS_COLUMN_FILTER_LABELS.acquirer,
      options: collectUniqueScalarValues(rows, (row) =>
        resolveDisplayName(
          row.acquirerName as string | undefined,
          lookups.acquirerById,
          Number(row.acquirerId) || 0,
        ),
      ),
    },
    {
      key: 'country',
      label: TERMINALS_COLUMN_FILTER_LABELS.country,
      options: collectUniqueScalarValues(rows, (row) =>
        resolveDisplayName(
          row.countryName as string | undefined,
          lookups.countryNameById,
          Number(row.countryId) || 0,
        ),
      ),
    },
    {
      key: 'currencies',
      label: TERMINALS_COLUMN_FILTER_LABELS.currencies,
      options: collectTerminalCurrencyFilterOptions(sourceRows, lookups.currencyById),
    },
    {
      key: 'flow',
      label: TERMINALS_COLUMN_FILTER_LABELS.flow,
      options: collectUniqueScalarValues(rows, (row) => String(row.flowName || '—')),
    },
    {
      key: 'industry',
      label: TERMINALS_COLUMN_FILTER_LABELS.industry,
      options: collectUniqueScalarValues(rows, (row) => String(row.industryName || '—')),
    },
    {
      key: 'traffic',
      label: TERMINALS_COLUMN_FILTER_LABELS.traffic,
      options: collectUniqueScalarValues(rows, (row) => String(row.trafficName || '—')),
    },
    CREATED_AT_FILTER_FIELD,
  ];
}

function CountryCellRenderer({
  countryLabel,
  countryFlag,
  countryId,
}: {
  countryLabel: string;
  countryFlag?: string;
  countryId?: number;
}) {
  if (!countryLabel || countryLabel === '—') {
    return (
      <GridCellWrap>
        <span className="text-muted-foreground">—</span>
      </GridCellWrap>
    );
  }
  return (
    <GridCellWrap>
      <span className="inline-flex items-center gap-2">
        {countryFlag && countryId ? (
          <CountryFlag
            countryFlag={countryFlag}
            countryId={countryId}
            countryName={countryLabel}
          />
        ) : null}
        <span className="text-muted-foreground">{countryLabel}</span>
      </span>
    </GridCellWrap>
  );
}

function CurrenciesCellRenderer(
  params: ICellRendererParams<MerchantTerminalGridRow | AcquirerTerminalGridRow>,
) {
  const ctx = params.context as TerminalsGridContext;
  const codes = params.data?.currencies || [];
  return (
    <ChipListCell
      items={codes}
      title="Currencies"
      isCurrency
      onShowMore={ctx.onShowMore}
    />
  );
}

export function createMappingColumnDefs(): ColDef<MappingGridRow>[] {
  return [
    {
      field: 'id',
      colId: 'mappingId',
      headerName: MAPPING_COLUMN_HEADERS.mappingId,
      width: 80,
      filterValueGetter: (params) =>
        formatMappingIdLabel(Number(params.data?.id) || 0),
      valueFormatter: (params) =>
        params.value != null ? `Mapping #${params.value}` : '—',
      cellRenderer: SubtitleCellRenderer,
    },
    {
      field: 'priority',
      colId: 'priority',
      headerName: MAPPING_COLUMN_HEADERS.priority,
      width: 110,
      filterValueGetter: (params) =>
        formatMappingPriorityLabel(params.data?.priority),
      cellRenderer: (params: ICellRendererParams<MappingGridRow>) => (
        <GridCellWrap>
          <span className="text-muted-foreground">
            {params.data?.priority ? `P${params.data.priority}` : '—'}
          </span>
        </GridCellWrap>
      ),
    },
    {
      field: 'merchantTerminalName',
      colId: 'merchantTerminal',
      headerName: MAPPING_COLUMN_HEADERS.merchantTerminal,
      flex: 2,
      minWidth: 220,
      cellRenderer: (params: ICellRendererParams<MappingGridRow>) => {
        if (!params.data) return null;
        return (
          <NameAvatarCell
            name={params.data.merchantTerminalName}
            accent={params.data.accent}
            muted
          />
        );
      },
    },
    {
      field: 'acquirerTerminalName',
      colId: 'acquirerTerminal',
      headerName: MAPPING_COLUMN_HEADERS.acquirerTerminal,
      flex: 1.5,
      minWidth: 200,
      cellRenderer: SubtitleCellRenderer,
    },
    {
      field: 'merchantName',
      colId: 'merchant',
      headerName: MAPPING_COLUMN_HEADERS.merchantName,
      flex: 1.5,
      minWidth: 180,
      cellRenderer: SubtitleCellRenderer,
    },
    {
      field: 'acquirerName',
      colId: 'acquirer',
      headerName: MAPPING_COLUMN_HEADERS.acquirerName,
      flex: 1.5,
      minWidth: 180,
      cellRenderer: SubtitleCellRenderer,
    },
    {
      field: 'volume',
      colId: 'volume',
      headerName: MAPPING_COLUMN_HEADERS.volume,
      flex: 1,
      minWidth: 140,
      filterValueGetter: (params) =>
        formatSuccessTransactionVolume(Number(params.data?.volume) || 0),
      cellRenderer: (params: ICellRendererParams<MappingGridRow>) => {
        if (!params.data) return null;
        return (
          <GridCellWrap>
            <div className="acq-vol-wrap">
              <span className="text-muted-foreground">
                {formatSuccessTransactionVolume(params.data.volume)}
              </span>
              <div className="acq-vol-bar-track">
                <div
                  className="acq-vol-bar-fill"
                  style={{
                    width: `${params.data.volPct}%`,
                    background: params.data.accent,
                  }}
                />
              </div>
            </div>
          </GridCellWrap>
        );
      },
    },
    {
      field: 'status',
      headerName: MAPPING_COLUMN_HEADERS.status,
      width: 108,
      cellRenderer: StatusBadgeCellRenderer,
      pinned:'right'
    },
    createdAtColumnDef<MappingGridRow>(),
    updatedAtColumnDef<MappingGridRow>(),
    {
      headerName: 'Action',
      width: 88,
      minWidth: 88,
      maxWidth: 88,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (params: ICellRendererParams<MappingGridRow>) => (
        <EditDeleteActionsCellRenderer
          {...params}
          context={{
            actions: (params.context as TerminalsGridContext).mappingActions,
          }}
        />
      ),
    },
  ];
}

export function createMerchantTerminalColumnDefs(): ColDef<MerchantTerminalGridRow>[] {
  return [
    {
      field: 'terminalId',
      colId: 'merchantTerminalId',
      headerName: MERCHANT_TERMINAL_COLUMN_HEADERS.merchantTerminalId,
      width: 80,
      filterValueGetter: (params) =>
        formatMerchantTerminalIdLabel(Number(params.data?.terminalId) || 0),
      valueFormatter: (params) =>
        params.value != null ? `Terminal #${params.value}` : '—',
      cellRenderer: SubtitleCellRenderer,
    },
    {
      field: 'name',
      headerName: MERCHANT_TERMINAL_COLUMN_HEADERS.name,
      flex: 1.5,
      minWidth: 260,
      cellRenderer: (params: ICellRendererParams<MerchantTerminalGridRow>) => {
        if (!params.data) return null;
        return (
          <NameAvatarCell name={params.data.name} accent={params.data.accent} muted />
        );
      },
    },
    {
      field: 'merchantName',
      colId: 'merchant',
      headerName: MERCHANT_TERMINAL_COLUMN_HEADERS.merchantName,
      flex: 1,
      minWidth: 200,
      cellRenderer: SubtitleCellRenderer,
    },
    {
      colId: 'country',
      headerName: MERCHANT_TERMINAL_COLUMN_HEADERS.country,
      flex: 1,
      minWidth: 180,
      filterValueGetter: (params) => params.data?.countryLabel || '',
      cellRenderer: (params: ICellRendererParams<MerchantTerminalGridRow>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <CountryCellRenderer
            countryLabel={row.countryLabel}
            countryFlag={row.countryFlag}
            countryId={row.countryId}
          />
        );
      },
    },
    {
      colId: 'currencies',
      headerName: MERCHANT_TERMINAL_COLUMN_HEADERS.currencies,
      flex: 1,
      minWidth: 180,
      filterValueGetter: (params) => joinFilterValues(params.data?.currencies),
      cellRenderer: CurrenciesCellRenderer,
    },
    {
      field: 'flowName',
      colId: 'flow',
      headerName: MERCHANT_TERMINAL_COLUMN_HEADERS.flow,
      width: 180,
      cellRenderer: CategoryChipCellRenderer,
    },
    {
      field: 'industryName',
      colId: 'industry',
      headerName: MERCHANT_TERMINAL_COLUMN_HEADERS.industry,
      width: 180,
      cellRenderer: CategoryChipCellRenderer,
    },
    {
      field: 'trafficName',
      colId: 'traffic',
      headerName: MERCHANT_TERMINAL_COLUMN_HEADERS.traffic,
      width: 180,
      cellRenderer: CategoryChipCellRenderer,
    },
    successTransactionVolumeColumnDef<MerchantTerminalGridRow>(
      MERCHANT_TERMINAL_COLUMN_HEADERS.successTransactionVolume,
    ),
    {
      field: 'notes',
      headerName: MERCHANT_TERMINAL_COLUMN_HEADERS.notes,
      flex: 1,
      minWidth: 240,
      cellRenderer: SubtitleCellRenderer,
    },
    createdAtColumnDef<MerchantTerminalGridRow>(),
    updatedAtColumnDef<MerchantTerminalGridRow>(),
    {
      headerName: 'Action',
      width: 88,
      minWidth: 88,
      maxWidth: 88,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (params: ICellRendererParams<MerchantTerminalGridRow>) => (
        <EditDeleteActionsCellRenderer
          {...params}
          context={{
            actions: {
              onEdit: () =>
                (params.context as TerminalsGridContext).terminalActions.onEdit(
                  params.data?.raw,
                ),
              onDelete: () =>
                (params.context as TerminalsGridContext).terminalActions.onDelete(
                  params.data?.raw,
                ),
            },
          }}
        />
      ),
    },
  ];
}

export function createAcquirerTerminalColumnDefs(): ColDef<AcquirerTerminalGridRow>[] {
  return [
    {
      field: 'terminalId',
      colId: 'acquirerTerminalId',
      headerName: ACQUIRER_TERMINAL_COLUMN_HEADERS.acquirerTerminalId,
      width: 80,
      filterValueGetter: (params) =>
        formatAcquirerTerminalIdLabel(Number(params.data?.terminalId) || 0),
      valueFormatter: (params) =>
        params.value != null ? `Terminal #${params.value}` : '—',
      cellRenderer: SubtitleCellRenderer,
    },
    {
      field: 'name',
      headerName: ACQUIRER_TERMINAL_COLUMN_HEADERS.name,
      flex: 1.5,
      minWidth: 260,
      cellRenderer: (params: ICellRendererParams<AcquirerTerminalGridRow>) => {
        if (!params.data) return null;
        return (
          <NameAvatarCell name={params.data.name} accent={params.data.accent} muted />
        );
      },
    },
    {
      field: 'acquirerName',
      colId: 'acquirer',
      headerName: ACQUIRER_TERMINAL_COLUMN_HEADERS.acquirerName,
      flex: 1,
      minWidth: 200,
      cellRenderer: SubtitleCellRenderer,
    },
    {
      colId: 'country',
      headerName: ACQUIRER_TERMINAL_COLUMN_HEADERS.country,
      flex: 1,
      minWidth: 180,
      filterValueGetter: (params) => params.data?.countryLabel || '',
      cellRenderer: (params: ICellRendererParams<AcquirerTerminalGridRow>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <CountryCellRenderer
            countryLabel={row.countryLabel}
            countryFlag={row.countryFlag}
            countryId={row.countryId}
          />
        );
      },
    },
    {
      colId: 'currencies',
      headerName: ACQUIRER_TERMINAL_COLUMN_HEADERS.currencies,
      flex: 1,
      minWidth: 180,
      filterValueGetter: (params) => joinFilterValues(params.data?.currencies),
      cellRenderer: CurrenciesCellRenderer,
    },
    {
      field: 'flowName',
      colId: 'flow',
      headerName: ACQUIRER_TERMINAL_COLUMN_HEADERS.flow,
      width: 180,
      cellRenderer: CategoryChipCellRenderer,
    },
    {
      field: 'industryName',
      colId: 'industry',
      headerName: ACQUIRER_TERMINAL_COLUMN_HEADERS.industry,
      width: 180,
      cellRenderer: CategoryChipCellRenderer,
    },
    {
      field: 'trafficName',
      colId: 'traffic',
      headerName: ACQUIRER_TERMINAL_COLUMN_HEADERS.traffic,
      width: 180,
      cellRenderer: CategoryChipCellRenderer,
    },
    successTransactionVolumeColumnDef<AcquirerTerminalGridRow>(
      ACQUIRER_TERMINAL_COLUMN_HEADERS.successTransactionVolume,
    ),
    createdAtColumnDef<AcquirerTerminalGridRow>(),
    updatedAtColumnDef<AcquirerTerminalGridRow>(),
    {
      headerName: 'Action',
      width: 88,
      minWidth: 88,
      maxWidth: 88,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (params: ICellRendererParams<AcquirerTerminalGridRow>) => (
        <EditDeleteActionsCellRenderer
          {...params}
          context={{
            actions: {
              onEdit: () =>
                (params.context as TerminalsGridContext).terminalActions.onEdit(
                  params.data?.raw,
                ),
              onDelete: () =>
                (params.context as TerminalsGridContext).terminalActions.onDelete(
                  params.data?.raw,
                ),
            },
          }}
        />
      ),
    },
  ];
}

export function createTerminalsGridOptions(
  emptyMessage: string,
): GridOptions<MappingGridRow | MerchantTerminalGridRow | AcquirerTerminalGridRow> {
  return {
    ...commonGridOptions,
    suppressRowClickSelection: true,
    overlayNoRowsTemplate: `<span class="text-sm text-muted-foreground">${emptyMessage}</span>`,
  };
}
